<template>
  <div>
    <!-- 麵包屑 -->
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{ name: 'DashboardHome' }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>參數設定</b-breadcrumb-item>
            <b-breadcrumb-item active>LUSH 會員查詢</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>

    <b-card>
      <div class="row mb-2 align-items-end">
        <div class="col-8 d-flex items-center">
          <h4 class="font-weight-bold">LUSH 會員查詢</h4>
        </div>
      </div>

      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
      >
        <!-- 信箱查詢 -->
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
        >
          <template #label>
            <b-form-radio
              v-model="searchType"
              value="email"
              @change="handleSearchTypeChange"
            >
              信箱
            </b-form-radio>
          </template>
          <b-form-input
            type="email"
            v-model="emailQuery"
            placeholder="請輸入信箱"
            :disabled="searchType !== 'email'"
            @keyup.enter="handleSearch"
          />
        </b-form-group>

        <!-- 會員 ID 查詢 -->
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
        >
          <template #label>
            <b-form-radio
              v-model="searchType"
              value="customer_id"
              @change="handleSearchTypeChange"
            >
              會員ID
            </b-form-radio>
          </template>
          <b-form-input
            type="text"
            v-model="customerIdQuery"
            placeholder="請輸入會員ID Ex. 12345678"
            :disabled="searchType !== 'customer_id' || isCustomerIdFromApi"
            @keyup.enter="handleSearch"
          />
        </b-form-group>

        <!-- 查詢按鈕 -->
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
        >
          <b-button
            variant="info"
            :disabled="isLoading || !canSearch"
            @click="handleSearch"
          >
            查詢
          </b-button>
        </b-form-group>

        <!-- 結果顯示 -->
        <editor
          class="mb-2 mr-sm-2 mb-sm-0"
          v-model="result"
          width="100%"
          height="400px"
          @init="editorInit"
          lang="json"
          theme="chrome"
          :disabled="!editable"
        />
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import editor from "vue2-ace-editor";
import lushApi from "@/apis/lush";

export default {
  components: { editor },

  data() {
    return {
      // 搜尋類型：email / customer_id
      searchType: "email",

      emailQuery: "",
      customerIdQuery: "",

      // 標記 customerId 是否由 API 帶回（鎖定輸入）
      isCustomerIdFromApi: false,

      result: "",
      isLoading: false,
      isSubmitting: false,
      canEdit: false,
    };
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),

    orgId() {
      return this.organization.id;
    },

    editable() {
      return this.canEdit;
    },

    canSearch() {
      if (this.searchType === "email") {
        return this.emailQuery && this.emailQuery.trim().length > 0;
      }

      return this.customerIdQuery && this.customerIdQuery.trim().length > 0;
    },
  },

  watch: {
    emailQuery() {
      if (this.searchType === "email" && this.isCustomerIdFromApi) {
        this.customerIdQuery = "";
        this.isCustomerIdFromApi = false;
      }
    },
  },

  mounted() {
    if (!this.checkPermission([consts.LUSH_SETTING_QUERY])) {
      this.$swal
        .fire({
          type: "error",
          text: "你沒有權限訪問此頁面",
          confirmButtonColor: "#d33",
        })
        .then(() => {
          this.$router.push({ name: "DashboardHome" });
        });
    }
  },

  methods: {
    editorInit() {
      require("brace/ext/language_tools");
      require("brace/mode/json");
      require("brace/mode/html");
      require("brace/theme/chrome");
    },

    handleSearchTypeChange() {
      this.emailQuery = "";
      this.customerIdQuery = "";
      this.isCustomerIdFromApi = false;
      this.result = "";
    },

    async handleSearch() {
      const isEmailSearch = this.searchType === "email";
      const query = isEmailSearch
        ? this.emailQuery.trim()
        : this.customerIdQuery.trim();

      if (!query) {
        this.$swal.fire({
          type: "warning",
          text: isEmailSearch ? "請輸入信箱" : "請輸入會員ID",
        });
        return;
      }

      // Email 格式驗證
      if (
        isEmailSearch &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query)
      ) {
        this.$swal.fire({
          type: "warning",
          text: "請輸入正確的信箱格式",
        });
        return;
      }

      try {
        this.isLoading = true;
        this.result = "查詢中...";

        const params = {};

        if (isEmailSearch) {
          params.email = query;
        } else {
          params.customer_id = query;
        }

        const response = await lushApi.query(params);

        if (isEmailSearch && response.data?.customer_id) {
          this.customerIdQuery = response.data.customer_id;
          this.isCustomerIdFromApi = true;
        }

        this.result = JSON.stringify(
          response.data?.data ?? response.data,
          null,
          2
        );
      } catch (error) {
        this.result = JSON.stringify(
          error.response?.data || {
            error: "查詢失敗",
            message: error.message,
          },
          null,
          2
        );
      } finally {
        this.isLoading = false;
      }
    },

    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>

<style scoped lang="scss">
.group + .group {
  border-top: 1px solid #cdd6dc !important;
}
</style>
